/**
 * PackageHoverPreview — wraps any trigger (usually a package name /
 * chip) with a shadcn HoverCard that shows a compact PackagePreviewPanel
 * plus a "Quick view" button that pops the draggable modal.
 *
 * Usage:
 *   <PackageHoverPreview pid={p.id}>
 *     <span>{p.name}</span>
 *   </PackageHoverPreview>
 *
 * The trigger element is rendered inline; no wrapping div so it can be
 * dropped into tables, chips, timeline cells etc. without breaking
 * layout.
 */
import { useState } from "react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import PackagePreviewPanel from "./PackagePreviewPanel";
import PackageQuickViewModal from "./PackageQuickViewModal";

export default function PackageHoverPreview({ pid, children, side = "right", align = "start", disabled = false }) {
  const [quick, setQuick] = useState(false);

  if (disabled || !pid) return <>{children}</>;

  return (
    <>
      <HoverCard openDelay={280} closeDelay={120}>
        <HoverCardTrigger asChild>
          {children}
        </HoverCardTrigger>
        <HoverCardContent side={side} align={align}
                          collisionPadding={16}
                          avoidCollisions={true}
                          className="w-[380px] p-0 rounded-none overflow-hidden text-[10.5px]"
                          data-testid={`pkg-hover-preview-${pid}`}>
          {/* Feb 2026 — Tightened width + max-height so long packages
              stay readable without pushing off-screen. `avoidCollisions`
              lets Radix flip the card left/up when a page corner is
              near the trigger. */}
          <div className="max-h-[min(70vh,560px)] flex flex-col">
            <div className="flex-1 min-h-0 overflow-hidden">
              <PackagePreviewPanel pid={pid} compact tight />
            </div>
            <div className="px-2 py-1 border-t border-border bg-muted/30 flex items-center justify-end">
              <Button variant="outline" size="sm"
                      className="h-6 text-[10px] rounded-none px-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setQuick(true);
                      }}
                      data-testid={`pkg-hover-quickview-btn-${pid}`}>
                <Eye className="w-3 h-3 mr-1" /> Quick view
              </Button>
            </div>
          </div>
        </HoverCardContent>
      </HoverCard>
      {quick && <PackageQuickViewModal pid={pid} onClose={() => setQuick(false)} />}
    </>
  );
}
